import { useCallback, useEffect, useRef, useState } from 'react'

import type { ApolloError } from '@apollo/client'
import { useLazyQuery, useMutation } from '@apollo/client'

import { navigate, routes } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_CURRENT_EMPLOYEE_ID } from 'src/graphql/users.graphql'
import { FIND_VOE_COMPANY_FOR_SWITCH } from 'src/graphql/voe.graphql'

export interface VoECompanySwitchTarget {
  voeId: string
  driverName?: string | null
  companyId: string
  companyName: string
  employeeId: string
}

export const useVoECompanySwitch = (voeId: string) => {
  const { currentUser } = useAuth()
  const [switchTarget, setSwitchTarget] =
    useState<VoECompanySwitchTarget | null>(null)
  const resolvedVoeIdRef = useRef<string | null>(null)

  useEffect(() => {
    resolvedVoeIdRef.current = null
  }, [voeId])

  const goToVoERequests = useCallback(() => {
    navigate(routes.voeRequests())
  }, [])

  const [findVoECompany] = useLazyQuery(FIND_VOE_COMPANY_FOR_SWITCH, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const target = data?.findVoECompanyForSwitch
      if (target) {
        setSwitchTarget(target)
      } else {
        goToVoERequests()
      }
    },
    onError: () => {
      toast('Unable to get VoE, please try again.', 'error')
      goToVoERequests()
    },
  })

  const [updateCurrentEmployeeId, { loading: isSwitching }] = useMutation(
    UPDATE_CURRENT_EMPLOYEE_ID,
    {
      onCompleted: (data) => {
        if (!data?.updateCurrentEmployeeId) {
          toast('Unable to switch companies. Please try again.', 'error')
          return
        }
        window.location.reload()
      },
      onError: () => {
        toast('Unable to switch companies. Please try again.', 'error')
      },
    }
  )

  const handleVoELoadError = useCallback(
    (error?: ApolloError) => {
      const isNotFound =
        !!error?.graphQLErrors?.length &&
        error.graphQLErrors.some((graphQLError) =>
          /not found/i.test(graphQLError.message)
        )

      if (!isNotFound) {
        toast('Unable to get VoE, please try again.', 'error')
        return
      }

      if (!currentUser?.isInternalAdmin) {
        goToVoERequests()
        return
      }
      if (resolvedVoeIdRef.current === voeId) {
        return
      }
      resolvedVoeIdRef.current = voeId
      findVoECompany({ variables: { voeId } })
    },
    [currentUser, voeId, findVoECompany, goToVoERequests]
  )

  const confirmSwitch = useCallback(() => {
    if (!switchTarget) return
    updateCurrentEmployeeId({
      variables: { input: { currentEmployeeId: switchTarget.employeeId } },
    })
  }, [switchTarget, updateCurrentEmployeeId])

  const cancelSwitch = useCallback(() => {
    setSwitchTarget(null)
    goToVoERequests()
  }, [goToVoERequests])

  return {
    handleVoELoadError,
    switchTarget,
    isSwitching,
    confirmSwitch,
    cancelSwitch,
  }
}

export default useVoECompanySwitch
